import cx from 'classnames';
import React, { ReactNode, RefObject, useRef } from 'react';
import {
  DismissButton,
  mergeProps,
  OverlayContainer,
  useOverlay,
  useOverlayPosition,
} from 'react-aria';

type AnchoredPanelProps = {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  className?: string;
  offset?: number;
  onRequestClose: () => void;
  open: boolean;
};

export const AnchoredPanel = ({
  anchorRef,
  children,
  className,
  offset = 8,
  onRequestClose,
  open,
}: AnchoredPanelProps) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  const { overlayProps } = useOverlay(
    {
      isDismissable: true,
      isOpen: open,
      onClose: onRequestClose,
      shouldCloseOnBlur: false,
      // The event's areas dropdown is rendered via `createPortal` (outside the panel DOM tree),
      // so React Aria would otherwise treat clicks inside that dropdown as outside interactions and close the share menu.
      // The discard-changes confirmation modal is likewise rendered outside the panel,
      // so clicks inside it must not be treated as an outside interaction either.
      // This is a workaround to prevent that.
      shouldCloseOnInteractOutside: element =>
        !element.closest('[data-publish-to-events-dropdown]') &&
        !element.closest('[role="dialog"][aria-modal="true"]'),
    },
    overlayRef,
  );

  const { overlayProps: positionProps } = useOverlayPosition({
    isOpen: open,
    offset,
    onClose: onRequestClose,
    overlayRef,
    placement: 'bottom end',
    shouldFlip: true,
    targetRef: anchorRef,
  });

  if (!open) {
    return null;
  }

  return (
    <OverlayContainer>
      <div
        {...mergeProps(overlayProps, positionProps)}
        ref={overlayRef}
        style={{
          ...(positionProps.style ?? {}),
          zIndex: 20,
        }}
        role="dialog"
        aria-modal="false"
        className={cx(
          'min-w-96 overflow-hidden',
          'rounded-lg border border-gray-600',
          'bg-spring-neutral-base shadow-2xl shadow-black text-spring-neutral-b0',
          className,
        )}
      >
        <DismissButton onDismiss={onRequestClose} />
        {children}
        <DismissButton onDismiss={onRequestClose} />
      </div>
    </OverlayContainer>
  );
};
